import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { ACTIVITY_STATUS_LABELS, ACTIVITY_TYPES, ACTIVITY_TYPE_LABELS } from "./labels";

const fmtDay = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch {
    return iso || "";
  }
};

function ActivityCard({ activity, contactId, apply, isFocused = false, innerRef = null }) {
  const [busy, setBusy] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [date, setDate] = useState(activity.due_date || "");
  const [time, setTime] = useState(activity.due_time || "");

  const patch = async (payload, message) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/contacts/${contactId}/activities/${activity.id}`, payload);
      toast.success(message);
      apply(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const open = activity.status === "aperta";

  return (
    <div
      ref={isFocused ? innerRef : undefined}
      className={`border p-4 ${open ? "border-[#d4af37]/50 bg-[#d4af37]/8" : "border-[#c9a45c]/18 bg-[#141414] opacity-70"} ${
        isFocused ? "ring-2 ring-[#d4af37]/70" : ""
      }`}
      data-testid={`activity-card-${activity.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-cream" data-testid={`activity-label-${activity.id}`}>
            {ACTIVITY_TYPE_LABELS[activity.type] || activity.type}
            {activity.description ? <span className="ml-2 font-normal text-[#c9c6bf]">— {activity.description}</span> : null}
          </p>
          <p className="mt-1 font-serif text-[14px] text-gold-light">
            {fmtDay(activity.due_date)} · ore {activity.due_time}
          </p>
        </div>
        <span
          className={`border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${
            open ? "border-[#d4af37]/50 text-gold-light" : "border-[#c9a45c]/25 text-[#929292]"
          }`}
          data-testid={`activity-status-${activity.id}`}
        >
          {ACTIVITY_STATUS_LABELS[activity.status] || activity.status}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {open ? (
          <>
            <button
              onClick={() => patch({ status: "completata" }, "Attività completata")}
              disabled={busy}
              className="btn btn-gold !min-h-[36px] !px-4 !text-[10px] disabled:opacity-60"
              data-testid={`activity-complete-${activity.id}`}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Completa
            </button>
            <button
              onClick={() => setRescheduling((v) => !v)}
              className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#c9c6bf] underline-offset-2 hover:text-gold-light hover:underline"
              data-testid={`activity-reschedule-${activity.id}`}
            >
              Riprogramma
            </button>
            <button
              onClick={() => patch({ status: "annullata" }, "Attività annullata")}
              className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#c9c6bf] underline-offset-2 hover:text-[#e07a5f] hover:underline"
              data-testid={`activity-cancel-${activity.id}`}
            >
              Annulla
            </button>
          </>
        ) : activity.status === "annullata" ? (
          <button
            onClick={() => patch({ status: "aperta" }, "Attività riattivata")}
            className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#c9c6bf] hover:text-gold-light"
            data-testid={`activity-reopen-${activity.id}`}
          >
            <RotateCcw size={11} /> Riattiva
          </button>
        ) : null}
      </div>
      {rescheduling && (
        <div className="mt-3 border border-[#c9a45c]/25 bg-[#161616] p-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-[11px] text-[#c9c6bf]">
              <span className="mb-1 block text-[9px] uppercase tracking-[0.14em] text-[#a8a5a0]">Nuova data</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="field !w-auto !py-2 !text-[12px]" data-testid={`activity-new-date-${activity.id}`} />
            </label>
            <label className="text-[11px] text-[#c9c6bf]">
              <span className="mb-1 block text-[9px] uppercase tracking-[0.14em] text-[#a8a5a0]">Nuova ora</span>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="field !w-auto !py-2 !text-[12px]" data-testid={`activity-new-time-${activity.id}`} />
            </label>
            <button
              onClick={async () => {
                await patch({ due_date: date, due_time: time }, "Attività riprogrammata");
                setRescheduling(false);
              }}
              disabled={busy || !date || !time}
              className="btn btn-gold !min-h-[36px] !px-4 !text-[10px] disabled:opacity-50"
              data-testid={`activity-reschedule-confirm-${activity.id}`}
            >
              Conferma
            </button>
            <button onClick={() => setRescheduling(false)} className="text-[10px] uppercase tracking-[0.12em] text-[#929292]" data-testid={`activity-reschedule-cancel-${activity.id}`}>
              Annulla modifica
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ActivitiesSection({ contact, apply, focusActivityId = null }) {
  const [form, setForm] = useState({ type: ACTIVITY_TYPES[0], due_date: "", due_time: "", description: "" });
  const [busy, setBusy] = useState(false);
  const focusRef = useRef(null);

  useEffect(() => {
    if (focusActivityId && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusActivityId]);

  const add = async () => {
    if (!form.due_date || !form.due_time) {
      toast.error("Scegli data e ora dell'attività");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/contacts/${contact.id}/activities`, form);
      toast.success("Attività inserita");
      apply(data);
      setForm({ type: ACTIVITY_TYPES[0], due_date: "", due_time: "", description: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const next = contact.next_activity;

  return (
    <div data-testid="activities-section">
      <p className="mt-6 text-[9px] font-bold uppercase tracking-[0.16em] text-[#a8a5a0]">Attività commerciali</p>

      {next && (
        <div className="mt-2 border border-[#d4af37]/60 bg-[#d4af37]/10 p-4" data-testid="next-activity-card">
          <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-gold-light">Prossima attività</p>
          <p className="mt-2 text-[14px] font-semibold text-cream" data-testid="next-activity-label">
            {ACTIVITY_TYPE_LABELS[next.type] || next.type}
            {next.description ? <span className="ml-2 font-normal text-[#c9c6bf]">— {next.description}</span> : null}
          </p>
          <p className="mt-1 font-serif text-[15px] text-gold-light" data-testid="next-activity-due">
            {fmtDay(next.due_date)} · ore {next.due_time}
          </p>
        </div>
      )}

      <div className="mt-3 grid gap-3">
        {(contact.activities || []).length === 0 && (
          <p className="text-[12px] text-[#777]" data-testid="activities-empty">
            Nessuna attività: inserisci un richiamo, un follow-up o un sopralluogo da svolgere.
          </p>
        )}
        {(contact.activities || []).map((a) => (
          <ActivityCard
            key={a.id}
            activity={a}
            contactId={contact.id}
            apply={apply}
            isFocused={focusActivityId === a.id}
            innerRef={focusRef}
          />
        ))}
      </div>

      <div className="mt-4 border border-[#c9a45c]/25 bg-[#161616] p-4" data-testid="activity-form">
        <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-gold-light">Nuova attività</p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="field !w-auto !py-2 !text-[12px]"
            data-testid="activity-type-select"
          >
            {ACTIVITY_TYPES.map((t) => (
              <option key={t} value={t}>{ACTIVITY_TYPE_LABELS[t]}</option>
            ))}
          </select>
          <input
            type="date"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            className="field !w-auto !py-2 !text-[12px]"
            data-testid="activity-date-input"
          />
          <input
            type="time"
            value={form.due_time}
            onChange={(e) => setForm({ ...form, due_time: e.target.value })}
            className="field !w-auto !py-2 !text-[12px]"
            data-testid="activity-time-input"
          />
          <input
            className="field min-w-[180px] flex-1 !py-2 !text-[12px]"
            placeholder="Breve descrizione (facoltativa)…"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="activity-description-input"
          />
          <button onClick={add} disabled={busy} className="btn btn-gold !min-h-[38px] !px-4 !text-[10px] disabled:opacity-60" data-testid="activity-add-button">
            {busy ? <Loader2 size={12} className="animate-spin" /> : null} Aggiungi attività
          </button>
        </div>
      </div>
    </div>
  );
}
